/**
 * Lightweight voice activity detection on PCM frames: RMS energy against an adaptive noise floor.
 * Pure functions — no audio is kept; only scalar state survives between frames.
 */
export interface VadConfig {
  /** Silence (after speech) that ends the command. Clinicians pause between terms: keep ≥ 1000 ms. */
  silenceThresholdMs: number;
  /** Speech shorter than this is treated as noise and does not arm end-of-command. */
  minSpeechMs: number;
  /** Hard stop regardless of speech. */
  maxCommandSeconds: number;
  /** Initial window used to estimate the noise floor. */
  calibrationMs: number;
  /** Frames after a level drop during which the detector still counts as speaking (smooths pauses). */
  hangoverMs: number;
  /** Absolute floor for the speech threshold (normalised RMS 0..1). */
  minThreshold: number;
  /** Speech threshold = clamp(noiseFloor * floorMultiplier, minThreshold, maxThreshold). */
  floorMultiplier: number;
  /** Upper bound of the speech threshold so a loud room can never make normal speech undetectable. */
  maxThreshold: number;
  /** Any frame at or above this level is speech regardless of the floor (also during calibration). */
  speechAbsolute: number;
  /** Upper bound of the tracked noise floor. */
  maxFloor: number;
}

export const DEFAULT_VAD_CONFIG: VadConfig = {
  silenceThresholdMs: 1200,
  minSpeechMs: 250,
  maxCommandSeconds: 30,
  calibrationMs: 300,
  hangoverMs: 200,
  minThreshold: 0.01,
  floorMultiplier: 3,
  maxThreshold: 0.03,
  speechAbsolute: 0.02,
  maxFloor: 0.01,
};

export interface VadState {
  elapsedMs: number;
  calibrating: boolean;
  noiseFloor: number;
  calibrationSum: number;
  calibrationFrames: number;
  /** Currently inside a speech segment (including hangover). */
  speaking: boolean;
  /** Accumulated speech in the current segment. */
  segmentSpeechMs: number;
  /** Total speech across the command. */
  totalSpeechMs: number;
  /** A segment ≥ minSpeechMs was observed, so trailing silence may end the command. */
  hasSpeech: boolean;
  /** Consecutive silence since the last above-threshold frame. */
  silenceMs: number;
  level: number;
  threshold: number;
  /** end-of-command already emitted; the detector stays quiet afterwards. */
  ended: boolean;
}

export type VadEvent = 'none' | 'speech-start' | 'end-of-command' | 'max-duration';

export interface VadResult {
  state: VadState;
  event: VadEvent;
}

export function createVadState(): VadState {
  return {
    elapsedMs: 0,
    calibrating: true,
    noiseFloor: 0,
    calibrationSum: 0,
    calibrationFrames: 0,
    speaking: false,
    segmentSpeechMs: 0,
    totalSpeechMs: 0,
    hasSpeech: false,
    silenceMs: 0,
    level: 0,
    threshold: DEFAULT_VAD_CONFIG.minThreshold,
    ended: false,
  };
}

/** Normalised RMS (0..1) of an Int16 or Float32 PCM frame. */
export function frameRms(frame: Int16Array | Float32Array): number {
  const n = frame.length;
  if (n === 0) return 0;
  let sum = 0;
  if (frame instanceof Int16Array) {
    for (let i = 0; i < n; i++) {
      const s = (frame[i] ?? 0) / 32768;
      sum += s * s;
    }
  } else {
    for (let i = 0; i < n; i++) {
      const s = frame[i] ?? 0;
      sum += s * s;
    }
  }
  return Math.sqrt(sum / n);
}

/** Duration in ms of a PCM frame at the given sample rate. */
export function frameDurationMs(samples: number, sampleRateHz: number): number {
  return (samples / sampleRateHz) * 1000;
}

/**
 * Feeds one frame's RMS level into the detector. Returns the next state and the event it produced.
 * `max-duration` takes precedence over `end-of-command`.
 */
export function processFrame(prev: VadState, config: VadConfig, rms: number, frameMs: number): VadResult {
  const state: VadState = { ...prev, elapsedMs: prev.elapsedMs + frameMs, level: rms };
  let event: VadEvent = 'none';

  if (state.elapsedMs >= config.maxCommandSeconds * 1000) {
    return { state, event: 'max-duration' };
  }

  const clampThreshold = (floor: number) => Math.min(Math.max(floor * config.floorMultiplier, config.minThreshold), config.maxThreshold);

  if (state.calibrating) {
    // The floor is the mean of the QUIET frames in the calibration window. Frames that look like speech
    // (≥ speechAbsolute) are excluded so a user who starts talking immediately ("Clear…", "Set…") cannot
    // poison the floor; they are also counted as speech straight away (fall through below).
    if (rms < config.speechAbsolute) {
      state.calibrationSum += rms;
      state.calibrationFrames += 1;
      state.noiseFloor = Math.min(state.calibrationSum / state.calibrationFrames, config.maxFloor);
    }
    state.threshold = clampThreshold(state.noiseFloor);
    if (state.elapsedMs >= config.calibrationMs) state.calibrating = false;
    if (rms < config.speechAbsolute) return { state, event };
  }

  const threshold = clampThreshold(state.noiseFloor);
  state.threshold = threshold;

  if (rms >= threshold || rms >= config.speechAbsolute) {
    if (!state.speaking) {
      state.speaking = true;
      state.segmentSpeechMs = 0;
    }
    state.segmentSpeechMs += frameMs;
    state.totalSpeechMs += frameMs;
    state.silenceMs = 0;
    if (!state.hasSpeech && state.segmentSpeechMs >= config.minSpeechMs) {
      state.hasSpeech = true;
      event = 'speech-start';
    }
  } else {
    state.silenceMs += frameMs;
    if (state.speaking && state.silenceMs > config.hangoverMs) {
      state.speaking = false;
      state.segmentSpeechMs = 0;
    }
    // Track the room only while genuinely quiet (not in a speech hangover), and never above maxFloor.
    if (!state.speaking) state.noiseFloor = Math.min(state.noiseFloor * 0.95 + rms * 0.05, config.maxFloor);
    if (state.hasSpeech && !state.ended && state.silenceMs >= config.silenceThresholdMs) {
      state.ended = true;
      event = 'end-of-command';
    }
  }

  return { state, event };
}
